<template>
  <!--
    AB#21297 — the dashboard's overflow menu.

    Export Order Data and Shipment Bookings each had a button of their own on the
    toolbar. Neither is used on most visits, and labelled buttons cost more width
    than the search field they sat beside. They move behind one ⋯, the same
    control the order detail pane uses for its second tier, so a person who has
    learned one has learned the other. (Assign order admin was removed from this
    menu; its prop/emit remain for the dashboard's existing wiring.)

    Permission gates travel with the items. When the signed-in role can do none
    of them, the menu is not rendered at all — a ⋯ that opens onto nothing
    is worse than no ⋯.
  -->
  <Dropdown
    v-if="hasItems"
    menu
    trigger-aria-label="More actions"
    trigger-style="dash-icon-btn"
    :close-on-outside-click="true"
  >
    <template #trigger>
      <EllipsisHorizontalIcon class="size-5" aria-hidden="true" />
    </template>
    <template #contents>
      <div class="dash-more-menu" data-test="dashboard-more-menu">
        <button
          v-if="canExport"
          role="menuitem"
          type="button"
          class="dash-more-item"
          data-test="more-export"
          @click="emit('export')"
        >
          Export order data
        </button>
        <button
          v-if="canShipment"
          role="menuitem"
          type="button"
          class="dash-more-item"
          data-test="more-shipment"
          :disabled="shipmentDisabled"
          :title="shipmentDisabled ? 'Nothing to book on this list yet' : undefined"
          @click="emit('shipment-bookings')"
        >
          Shipment bookings
        </button>
      </div>
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import Dropdown from '@/components/Shared/Dropdown.vue';
import { EllipsisHorizontalIcon } from '@heroicons/vue/24/outline';
import { computed } from 'vue';

const props = defineProps<{
  canAssignAdmin: boolean;
  canExport: boolean;
  canShipment: boolean;
  shipmentDisabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'assign-admin'): void;
  (e: 'export'): void;
  (e: 'shipment-bookings'): void;
}>();

// Assign order admin was removed from this menu; the prop/emit stay declared so
// the dashboard's existing binding and handler keep type-checking.
const hasItems = computed(() => props.canExport || props.canShipment);
</script>

<style lang="scss">
.dash-more-menu {
  @apply flex flex-col p-1.5 min-w-[200px];
}
.dash-more-item {
  @apply w-full text-left text-sm text-ink px-3 py-2 rounded-[6px] whitespace-nowrap
         bg-transparent border-0 transition-colors;
}
.dash-more-item:hover:not(:disabled),
.dash-more-item:focus-visible {
  @apply bg-surface-3 outline-none;
}
.dash-more-item:disabled {
  @apply text-ink-mute cursor-not-allowed;
}
</style>
